import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik' // Assuming you're using Formik
import { selectTranslationState } from '../../../store/slices/languageSlice'
import CustomInput from '../../../components/CustomInput'
import CustomSelect from '../../../components/CustomSelectInput'
import CustomButton from '../../../components/CustomButton'
import { AppDispatch, RootState } from '../../../store/store'
import useCustomStyles from '../../../hooks/useCustomStyles'
import CustomMultiSelect from '../../../components/CustomMultiSelect'
import {
  addClientAndAccessCode,
  getContractsByBusinesses,
} from '../../../store/actions/accessCodeActions'
import {
  addSevenDays,
  getBusinessNamesForAdmin,
  getCurrentDateFormatted,
} from '../../../utils'
import { useNavigate } from 'react-router-dom'
import { selectState } from '../../../store/slices/themeSlice'
import validationSchema from '../scripts'
import { header2Styles } from '../../../assets/styles/header2'
import { PendingClientType } from '../../../store/slices/clientsSlice'

interface AddNewClientModalProps {
  isOpen: boolean
  onConfirm: (client: PendingClientType) => void
  onCancel: () => void
  role_type: string // Optional prop to set the default role type
}
export interface Option {
  label: string
  value: string
}

const roleList = [
  // {label: 'Marketing', value: 'Marketing'},
  { label: 'CRM', value: 'CRM' },
  { label: 'Administrator', value: 'Administrator' },
]

const AddNewClientModal: React.FC<AddNewClientModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  role_type,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const { t } = useSelector(selectTranslationState)
  const { theme, activeTheme, textTheme } = useSelector(selectState)
  const { user } = useSelector((state: RootState) => state.auth_slice)
  const { contracts, loading, businesses } = useSelector(
    (state: RootState) => state.accessCode_slice,
  )
  const classes = useCustomStyles(header2Styles, theme)
  const [filteredContracts, setFilteredContracts] = useState(contracts)
  const [openLicenceCount, setOpenLicenceCount] = useState(0)
  const [contractExpiry, setContractExpiry] = useState<
    string | Date | undefined
  >('')
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  useEffect(() => {
    console.log('AddNewClientModal rendered')
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onCancel()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isOpen, onCancel])
  // useEffect(() => {
  //   // Define the function that updates the state (or dispatches a Redux action)
  //   const handleLicenseUpdate = (accessCode: AccessCodeType) => {
  //     // Example of dispatching a Redux action
  //     dispatch(setLicence(accessCode));
  //   };

  //   const updateContractsInState = (contracts: ContractType[]) => {
  //     // Dispatch an action to update the entire contracts list in the Redux store
  //     dispatch(setContracts(contracts));  // Ensure this action is set up in your Redux slice
  //   };
  //   // Start listening to licence validated updates
  //   const unsubscribeLicence = listenToLicenceValidatedUpdates(handleLicenseUpdate);

  //   // Start listening to licence count updates
  //   const unsubscribeContract = listenToLicenceCountUpdates(updateContractsInState);

  //   // Cleanup listener when the component unmounts
  //   return () => {
  //     if (unsubscribeLicence) {
  //       unsubscribeLicence();  // Unsubscribe from licence updates
  //     }
  //     if (unsubscribeContract) {
  //       unsubscribeContract();  // Unsubscribe from contract updates
  //     }
  //   };
  // }, []);

  const formik = useFormik({
    initialValues: {
      roles: [],
      role_type: role_type,
      user_name: '',
      clientId: '',
      email: '',
      codeType: 'licence-code',
      contract_ref: '',
      contract_left_licences: 0,
      business_client: user?.business?.id || '',
      business_name: '',
      partner: '',
      expire_at: addSevenDays(getCurrentDateFormatted()),
      useLimit: 1,
      number_of_codes_to_generate: 1,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log('submit formik values', values)
      try {
        const result = await dispatch(
          addClientAndAccessCode({
            client: {
              pending_user_name: values.user_name,
              pending_email: values.email,
              clientId: values.clientId,
              business_name: values.business_name,
            },
            accessCode: {
              role_type: values.role_type,
              roles: values.roles,
              expire_at: contractExpiry?.toString(),
              codeType: values.codeType,
              contract_ref: values.contract_ref,
              business_client: values.business_client,
              number_of_codes_to_generate: values.number_of_codes_to_generate,
              navigate,
            },
          }),
        ).unwrap()
        if (result?.newClient) {
          onConfirm(result?.newClient) // Call the onConfirm function to close the modal
          resetForm() // ✅ Reset the form after successful submission
        }
        console.log('Returned value:', result) // 🔥 You get the returned value here
      } catch (err) {
        console.error('Thunk failed:', err) // Handle any error here
      }
    },
    validateOnChange: false, // Disable validation on change
    validateOnBlur: true, // Disable validation on blur
  })

  const {
    values,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
    resetForm,
  } = formik

  useEffect(() => {
    if (!contracts && user?.business) {
      const businessNames: string[] = getBusinessNamesForAdmin(user)
      dispatch(getContractsByBusinesses({ business_names: businessNames }))
    }
  }, [])

  useEffect(() => {
    if (contracts) {
      if (role_type == 'advisor') {
        setFilteredContracts(
          contracts.filter(
            (contract) =>
              contract.business_client == user?.business?.id &&
              contract.useLimit - contract.licence_count > 0 &&
              contract.expire_at &&
              new Date(contract.expire_at) > new Date(),
          ),
        )
      } else {
        setFilteredContracts(
          contracts.filter(
            (contract) =>
              contract.useLimit - contract.licence_count > 0 &&
              contract.expire_at &&
              new Date(contract.expire_at) > new Date(),
          ),
        )
      }
    }
  }, [contracts])

  // Wrapper function to handle form submission from button click
  const handleFormSubmit = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    await formik.submitForm()
  }

  const handleContractRefChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    await setFieldValue(name, value)
    await setFieldTouched(name, true)
    const valid = await validationSchema.validateAt(name, { [name]: value })
    if (valid) {
      formik.setFieldError(name, undefined) // Clear the error if validation passes
      const selectedContract = filteredContracts?.filter(
        (item) => item.id == e.target.value,
      )[0]
      if (selectedContract) {
        setOpenLicenceCount(
          selectedContract.useLimit - selectedContract.licence_count,
        )
        setFieldValue(
          'contract_left_licences',
          selectedContract.useLimit - selectedContract.licence_count,
        )
        setFieldValue(
          'business_name',
          businesses?.filter(
            (business) => business.id == selectedContract.business_client,
          )[0]?.name || '',
        )
        setFieldValue('business_client', selectedContract.business_client)
        setContractExpiry(selectedContract.expire_at)
      }
    }
  }

  const handleSelectFieldChange2 = async (e: {
    name: string
    value: string | number | Option[]
  }) => {
    const { name, value } = e
    await setFieldValue(name, value)
    await setFieldTouched(name, true)
    // const valid = await formik.validateForm();
    // if(valid){
    //   console.log('field validated', name, value)
    // }else{
    //   console.log('field not validated', name, formik.errors)
    // }
  }

  if (isOpen) {
    return (
      <div className={classes.overlay}>
        <div className={classes.modal} ref={modalRef}>
          <h2 className={classes.headerTextLogOutModal}>
            {role_type == 'client' ? 'ADD NEW CLIENT' : 'ADD NEW ADVISOR'}
          </h2>

          <form onSubmit={handleSubmit} className={classes?.notificationForm}>
            <CustomInput
              label={t[textTheme].addClientScreen.form.userName.label}
              name="user_name"
              type="text"
              value={values.user_name}
              onChange={(e) => handleSelectFieldChange2(e.target)}
              error={formik.errors.user_name ? formik.errors.user_name : ''}
            />

            <CustomInput
              label={t[textTheme].addClientScreen.form.email.label}
              name="email"
              type="text"
              value={values.email}
              onChange={(e) => handleSelectFieldChange2(e.target)}
              error={formik.errors.email ? formik.errors.email : ''}
            />

            <CustomInput
              label={t[textTheme].addClientScreen.form.clientId.label}
              name="clientId"
              type="text"
              value={values.clientId}
              onChange={(e) => handleSelectFieldChange2(e.target)}
              error={formik.errors.clientId ? formik.errors.clientId : ''}
            />

            <CustomSelect
              label={t[textTheme].addClientScreen.form.contractRefInput.label}
              name="contract_ref"
              value={values.contract_ref}
              options={
                filteredContracts
                  ? filteredContracts
                      .filter((item) => item.contract_ref && item.id)
                      .map((item) => ({
                        name: item.contract_ref as string,
                        id: item.id as string,
                      }))
                  : []
              }
              onChange={async (e) => await handleContractRefChange(e)}
              // onBlure={handleBlur}
              placeholder={
                t[textTheme].addClientScreen.form.contractRefInput.placeholder
              }
              error={
                formik.errors.contract_ref ? formik.errors.contract_ref : ''
              }
            />

            {values.contract_ref && (
              <>
                <CustomInput
                  label={
                    t[textTheme].addClientScreen.form.remainingLicencesInput
                      .label
                  }
                  name="contract_left_licences"
                  type="number"
                  value={openLicenceCount}
                  onChange={() => {}}
                  disabled
                />

                <CustomInput
                  label={
                    t[textTheme].addClientScreen.form.subscriptionExpiryInput
                      .label
                  }
                  name="contract_expiry_date"
                  type="text"
                  value={contractExpiry}
                  onChange={() => {}}
                  disabled
                />
              </>
            )}
            {values.role_type === 'advisor' && (
              <CustomMultiSelect
                name="roles"
                value={values.roles}
                onChange={(value) =>
                  handleSelectFieldChange2({ name: 'roles', value: value })
                }
                options={roleList}
                label="Roles To Assign"
                error={
                  Array.isArray(formik.errors.roles)
                    ? formik.errors.roles.join(', ')
                    : formik.errors.roles || ''
                }
              />
            )}

            <CustomButton
              title={
                role_type == 'client'
                  ? t[textTheme].addClientScreen.form.generateButton.title
                  : 'ADD ADVISOR'
              }
              name="addClientButton"
              handleClick={(e) => {
                handleFormSubmit(e)
              }}
              type="submit"
              loading={loading}
              disabled={!formik.isValid || loading || !formik.dirty}
            />
          </form>
        </div>
      </div>
    )
  } else {
    return null
  }
}

export default AddNewClientModal
